import { useState, useEffect } from "react";
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  Send, 
  Check, 
  Clock, 
  X,
  Eye,
  RefreshCw,
  Loader2,
  FileSignature,
  Users,
  Link as LinkIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const CONTRACT_TYPES = [
  { value: "service_agreement", label: "Service Agreement" },
  { value: "model_release", label: "Model Release" },
  { value: "custom", label: "Custom Contract" }
];

const STATUS_BADGES = {
  pending: { label: "Pending", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  signed: { label: "Signed", className: "bg-green-500/20 text-green-400 border-green-500/30" },
  voided: { label: "Voided", className: "bg-red-500/20 text-red-400 border-red-500/30" },
  expired: { label: "Expired", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" }
};

// Template sections - structured data instead of raw HTML
const TEMPLATE_SECTIONS = {
  service_agreement: [
    { id: "services", label: "Services Description", type: "textarea", placeholder: "Describe the photography services to be provided..." },
    { id: "payment_terms", label: "Payment Terms", type: "textarea", placeholder: "Payment is due according to the terms specified in the invoice..." },
    { id: "cancellation_policy", label: "Cancellation Policy", type: "textarea", placeholder: "Cancellations made less than 48 hours before..." },
    { id: "delivery_timeline", label: "Image Delivery Timeline", type: "input", placeholder: "2-4 weeks after session" },
    { id: "usage_rights", label: "Usage Rights", type: "textarea", placeholder: "The Client receives a personal use license..." },
    { id: "liability", label: "Liability Clause", type: "textarea", placeholder: "The Photographer's liability is limited to..." }
  ],
  model_release: [
    { id: "usage_purposes", label: "Permitted Usage", type: "checkboxes", options: [
      "Portfolio and promotional materials",
      "Website and social media",
      "Print and digital advertising",
      "Editorial use",
      "Commercial licensing"
    ]},
    { id: "compensation", label: "Compensation Terms", type: "textarea", placeholder: "No additional compensation will be provided..." },
    { id: "restrictions", label: "Restrictions (if any)", type: "textarea", placeholder: "List any restrictions on image use..." },
    { id: "duration", label: "Duration of Release", type: "select", options: ["Perpetual", "1 Year", "2 Years", "5 Years"] }
  ],
  custom: [
    { id: "title", label: "Contract Title", type: "input", placeholder: "Enter contract title" },
    { id: "terms", label: "Terms & Conditions", type: "textarea", placeholder: "Enter the full contract terms..." }
  ]
};

const ContractsPage = () => {
  const [activeTab, setActiveTab] = useState("contracts");
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  
  // Template dialog
  const [templateDialog, setTemplateDialog] = useState({ open: false, template: null });
  const [templateForm, setTemplateForm] = useState({ 
    name: "", 
    description: "", 
    type: "service_agreement", 
    is_default: false,
    sections: {}
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Send contract dialog
  const [sendDialog, setSendDialog] = useState({ open: false, template: null });
  const [sendForm, setSendForm] = useState({ 
    client_name: "", 
    client_email: "", 
    booking_ids: [],
    send_immediately: true 
  });
  const [sending, setSending] = useState(false);
  
  // View contract dialog
  const [viewDialog, setViewDialog] = useState({ open: false, contract: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, contractsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/contracts/templates`, { withCredentials: true }),
        axios.get(`${API}/contracts`, { withCredentials: true }),
        axios.get(`${API}/bookings/requests`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setTemplates(templatesRes.data);
      setContracts(contractsRes.data);
      setBookings(bookingsRes.data || []);
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTemplates = async () => {
    try {
      const response = await axios.post(`${API}/contracts/templates/create-defaults`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to create templates");
    }
  };

  const openTemplateDialog = (template = null) => {
    if (template) {
      setTemplateForm({
        name: template.name,
        description: template.description || "",
        type: template.type,
        is_default: template.is_default,
        sections: template.sections || {}
      });
    } else {
      setTemplateForm({ 
        name: "", 
        description: "", 
        type: "service_agreement", 
        is_default: false,
        sections: {}
      });
    }
    setTemplateDialog({ open: true, template });
  };

  const updateSection = (sectionId, value) => {
    setTemplateForm(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: value
      }
    }));
  };

  const saveTemplate = async () => {
    if (!templateForm.name) {
      toast.error("Template name is required");
      return;
    }
    
    setSavingTemplate(true);
    try {
      const payload = {
        name: templateForm.name,
        description: templateForm.description,
        type: templateForm.type,
        is_default: templateForm.is_default,
        sections: templateForm.sections
      };

      if (templateDialog.template) {
        await axios.put(`${API}/contracts/templates/${templateDialog.template.template_id}`, payload, { withCredentials: true });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/contracts/templates`, payload, { withCredentials: true });
        toast.success("Template created");
      }
      setTemplateDialog({ open: false, template: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/contracts/templates/${templateId}`, { withCredentials: true });
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const openSendDialog = (template) => {
    setSendForm({ client_name: "", client_email: "", booking_ids: [], send_immediately: true });
    setSendDialog({ open: true, template });
  };

  const toggleBookingSelection = (bookingId) => {
    setSendForm(prev => {
      const newIds = prev.booking_ids.includes(bookingId)
        ? prev.booking_ids.filter(id => id !== bookingId)
        : [...prev.booking_ids, bookingId];
      
      // Auto-fill client info from first selected booking
      if (newIds.length > 0 && !prev.client_name) {
        const firstBooking = bookings.find(b => b.request_id === newIds[0]);
        if (firstBooking) {
          return {
            ...prev,
            booking_ids: newIds,
            client_name: firstBooking.customer_name,
            client_email: firstBooking.customer_email
          };
        }
      }
      
      return { ...prev, booking_ids: newIds };
    });
  };

  const sendContract = async () => {
    if (!sendForm.client_name || !sendForm.client_email) {
      toast.error("Client name and email are required");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/contracts`, {
        template_id: sendDialog.template.template_id,
        client_name: sendForm.client_name,
        client_email: sendForm.client_email,
        booking_ids: sendForm.booking_ids.length > 0 ? sendForm.booking_ids : null,
        send_immediately: sendForm.send_immediately
      }, { withCredentials: true });
      
      toast.success("Contract sent to client");
      setSendDialog({ open: false, template: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send contract");
    } finally {
      setSending(false);
    }
  };

  const resendContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/resend`, {}, { withCredentials: true });
      toast.success("Contract email resent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend");
    }
  };

  const voidContract = async (contractId) => {
    if (!confirm("Void this contract? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/contracts/${contractId}/void`, {}, { withCredentials: true });
      toast.success("Contract voided");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to void contract");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Get bookings that match the client email
  const getMatchingBookings = (email) => {
    if (!email) return [];
    return bookings.filter(b => 
      b.customer_email?.toLowerCase() === email.toLowerCase() &&
      b.status !== "declined"
    );
  };

  const renderTemplateFields = () => {
    const sections = TEMPLATE_SECTIONS[templateForm.type] || TEMPLATE_SECTIONS.custom;
    
    return (
      <div className="space-y-4">
        {sections.map((section) => (
          <div key={section.id} className="space-y-2">
            <Label>{section.label}</Label>
            
            {section.type === "input" && (
              <Input
                value={templateForm.sections[section.id] || ""}
                onChange={(e) => updateSection(section.id, e.target.value)}
                placeholder={section.placeholder}
              />
            )}
            
            {section.type === "textarea" && (
              <Textarea
                value={templateForm.sections[section.id] || ""}
                onChange={(e) => updateSection(section.id, e.target.value)}
                placeholder={section.placeholder}
                rows={3}
              />
            )}
            
            {section.type === "select" && (
              <Select
                value={templateForm.sections[section.id] || section.options[0]}
                onValueChange={(v) => updateSection(section.id, v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {section.options.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {section.type === "checkboxes" && (
              <div className="space-y-2 pl-2">
                {section.options.map((opt) => {
                  const checked = (templateForm.sections[section.id] || []).includes(opt);
                  return (
                    <div key={opt} className="flex items-center gap-2">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => {
                          const current = templateForm.sections[section.id] || [];
                          const newValue = c 
                            ? [...current, opt]
                            : current.filter(v => v !== opt);
                          updateSection(section.id, newValue);
                        }}
                      />
                      <span className="text-sm">{opt}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="contracts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif">Contracts & E-Signatures</h1>
            <p className="text-muted-foreground">Digital contracts and model releases</p>
          </div>
        </div>
        
        <Card className="bg-yellow-500/10 border-yellow-500/20">
          <CardContent className="py-8 text-center">
            <FileSignature className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-xl font-medium mb-2">Studio Feature</h3>
            <p className="text-muted-foreground mb-4">
              Contracts & E-Signatures are available on the Studio plan.
            </p>
            <Button onClick={() => window.location.href = '/pricing'}>
              Upgrade to Studio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif">Contracts & E-Signatures</h1>
          <p className="text-muted-foreground">Digital contracts and model releases</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-serif">{templates.length}</p>
            <p className="text-xs text-muted-foreground">Templates</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-serif text-yellow-400">
              {contracts.filter(c => c.status === "pending").length}
            </p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-serif text-green-400">
              {contracts.filter(c => c.status === "signed").length}
            </p>
            <p className="text-xs text-muted-foreground">Signed</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-serif">{contracts.length}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="space-y-4">
          {contracts.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No contracts yet</h3>
                <p className="text-muted-foreground mb-4">Send your first contract from a template</p>
                <Button onClick={() => setActiveTab("templates")}>
                  View Templates
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {contracts.map((contract) => (
                <Card key={contract.contract_id} className="bg-card border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${contract.status === "signed" ? "bg-green-500/10" : "bg-muted"}`}>
                          <FileText className={`w-5 h-5 ${contract.status === "signed" ? "text-green-500" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{contract.template_name}</h3>
                            <Badge className={STATUS_BADGES[contract.status]?.className}>
                              {STATUS_BADGES[contract.status]?.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {contract.client_name} • {contract.client_email}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-muted-foreground">
                              Sent: {formatDate(contract.created_at)}
                              {contract.signed_at && ` • Signed: ${formatDate(contract.signed_at)}`}
                            </p>
                            {contract.booking_ids?.length > 0 && (
                              <span className="flex items-center gap-1 text-xs text-primary">
                                <LinkIcon className="w-3 h-3" />
                                {contract.booking_ids.length} booking{contract.booking_ids.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setViewDialog({ open: true, contract })}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {contract.status === "pending" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => resendContract(contract.contract_id)}>
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-400"
                              onClick={() => voidContract(contract.contract_id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between">
            <Button variant="outline" onClick={createDefaultTemplates}>
              <Plus className="w-4 h-4 mr-2" />
              Create Default Templates
            </Button>
            <Button onClick={() => openTemplateDialog()} data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-4">Create templates for service agreements and model releases</p>
                <Button onClick={createDefaultTemplates}>
                  Create Default Templates
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <Card key={template.template_id} className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription>{template.description}</CardDescription>
                      </div>
                      {template.is_default && (
                        <Badge variant="outline">Default</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">
                        {CONTRACT_TYPES.find(t => t.value === template.type)?.label}
                      </Badge>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openTemplateDialog(template)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-400" onClick={() => deleteTemplate(template.template_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => openSendDialog(template)} data-testid={`send-contract-${template.template_id}`}>
                          <Send className="w-4 h-4 mr-2" />
                          Send
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Template Dialog - Now with structured fields */}
      <Dialog open={templateDialog.open} onOpenChange={(open) => !open && setTemplateDialog({ open: false, template: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{templateDialog.template ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>
              Fill in the fields below to create your contract template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Wedding Photography Agreement"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={templateForm.type}
                  onValueChange={(v) => setTemplateForm(prev => ({ ...prev, type: v, sections: {} }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of when to use this template"
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Contract Sections</h4>
              {renderTemplateFields()}
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={templateForm.is_default}
                onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, is_default: checked }))}
              />
              <Label>Set as default for this type</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog({ open: false, template: null })}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={savingTemplate}>
              {savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Contract Dialog - Now with booking selection */}
      <Dialog open={sendDialog.open} onOpenChange={(open) => !open && setSendDialog({ open: false, template: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Contract</DialogTitle>
            <DialogDescription>
              Send &quot;{sendDialog.template?.name}&quot; to a client for signing
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client Name *</Label>
              <Input
                value={sendForm.client_name}
                onChange={(e) => setSendForm(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Client Email *</Label>
              <Input
                type="email"
                value={sendForm.client_email}
                onChange={(e) => setSendForm(prev => ({ ...prev, client_email: e.target.value }))}
                placeholder="client@email.com"
              />
            </div>
            
            {/* Link to Bookings */}
            <div>
              <Label className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Link to Bookings (optional)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select bookings to associate with this contract. The client will see this contract in their portal.
              </p>
              
              {bookings.length > 0 ? (
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                  {bookings.filter(b => b.status !== "declined").slice(0, 20).map((booking) => (
                    <div 
                      key={booking.request_id} 
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        sendForm.booking_ids.includes(booking.request_id) 
                          ? "bg-primary/10 border border-primary/30" 
                          : "hover:bg-muted"
                      }`}
                      onClick={() => toggleBookingSelection(booking.request_id)}
                    >
                      <Checkbox 
                        checked={sendForm.booking_ids.includes(booking.request_id)}
                        onCheckedChange={() => toggleBookingSelection(booking.request_id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{booking.customer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.event_type} • {booking.event_date || "TBD"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground p-2 border rounded-lg">
                  No bookings available. Create a booking first to link contracts.
                </p>
              )}
              
              {sendForm.booking_ids.length > 0 && (
                <p className="text-xs text-primary mt-2">
                  {sendForm.booking_ids.length} booking{sendForm.booking_ids.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={sendForm.send_immediately}
                onCheckedChange={(checked) => setSendForm(prev => ({ ...prev, send_immediately: checked }))}
              />
              <Label>Send email immediately</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog({ open: false, template: null })}>
              Cancel
            </Button>
            <Button onClick={sendContract} disabled={sending}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Contract Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, contract: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewDialog.contract?.template_name}</DialogTitle>
            <DialogDescription>
              Sent to {viewDialog.contract?.client_name} on {formatDate(viewDialog.contract?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {viewDialog.contract?.status === "signed" && viewDialog.contract?.signature && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="font-medium text-green-400">Signed</span>
              </div>
              <p className="text-sm">
                <strong>Signed by:</strong> {viewDialog.contract.signature.typed_name}
              </p>
              <p className="text-sm">
                <strong>Date:</strong> {formatDate(viewDialog.contract.signature.signed_at)}
              </p>
              <p className="text-sm text-muted-foreground">
                IP: {viewDialog.contract.signature.signer_ip}
              </p>
            </div>
          )}
          
          {viewDialog.contract?.booking_ids?.length > 0 && (
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                <strong>Linked to {viewDialog.contract.booking_ids.length} booking{viewDialog.contract.booking_ids.length > 1 ? 's' : ''}</strong>
              </p>
            </div>
          )}
          
          <div 
            className="prose prose-invert max-w-none p-4 bg-muted/20 rounded-lg"
            dangerouslySetInnerHTML={{ __html: viewDialog.contract?.content }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsPage;
